import React, { useState } from "react";
import {
  FaRedoAlt,
  FaCamera,
  FaFileAlt,
  FaBan,
  FaCloudUploadAlt,
} from "react-icons/fa";
import { useAuth } from "../../AuthContext";
import api from "../../api";
import { messageOf } from "./SignatureApi";
import IdentityDocumentUpload from "./IdentityDocumentUpload";
import { canManageAuthentication } from "./authenticationManagement";
const actions = [
  [
    "RESTART",
    "Começar tudo do zero",
    "Solicita nova identificação, documento e foto. Também remove o bloqueio de assinaturas.",
    FaRedoAlt,
  ],
  [
    "DOCUMENT",
    "Pedir apenas o documento",
    "A pessoa reenvia o documento. A foto válida é preservada.",
    FaFileAlt,
  ],
  [
    "SELFIE",
    "Pedir apenas a foto",
    "A pessoa faz uma nova captura. O documento válido é preservado.",
    FaCamera,
  ],
  [
    "UPLOAD",
    "Substituir documento",
    "Envie um documento para este usuário. A nova versão será analisada e a autoria ficará registrada.",
    FaCloudUploadAlt,
  ],
  [
    "BLOCK",
    "Bloquear assinaturas",
    "Impede o uso das assinaturas até uma nova solicitação do responsável.",
    FaBan,
  ],
];
export default function AuthenticationActions({ userId, onChanged }) {
  const { user } = useAuth();
  const [action, setAction] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [success, setSuccess] = useState("");
  if (!canManageAuthentication(user)) return null;
  const confirm = async () => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      if (action === "UPLOAD") {
        const body = new FormData();
        body.append("file", file);
        body.append("note", note.trim());
        await api.post(
          `/signatures/enrollment/users/${userId}/document`,
          body,
          { timeout: 60000 },
        );
      } else
        await api.post(`/signatures/enrollment/users/${userId}/manage`, {
          action,
          note: note.trim(),
        });
      setSuccess(
        action === "UPLOAD"
          ? "Documento recebido. Acompanhe a análise na identificação."
          : "Solicitação registrada. A orientação aparecerá para a pessoa ao acessar Assinaturas.",
      );
      setAction("");
      setFile(null);
      setNote("");
      onChanged?.();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className="signature-card authentication-actions"
      aria-label="Gerenciar autenticação"
    >
      <span className="signature-eyebrow">PRÓXIMO PASSO</span>
      <h3>O que precisa ser feito?</h3>
      <p className="signature-help">
        Escolha o que a pessoa deve refazer. Os registros anteriores permanecem
        no histórico. Correções de um só item exigem os demais arquivos dentro
        do prazo de retenção.
      </p>
      <div className="authentication-action-grid">
        {actions.map(([key, title, help, Icon]) => (
          <button
            type="button"
            key={key}
            aria-pressed={action === key}
            disabled={busy}
            onClick={() => {
              setAction(key);
              setError("");
              setSuccess("");
            }}
          >
            <Icon aria-hidden="true" />
            <span>
              <strong>{title}</strong>
              <small>{help}</small>
            </span>
          </button>
        ))}
      </div>
      {action && (
        <div className="authentication-action-confirm">
          <strong>{actions.find((a) => a[0] === action)?.[1]}</strong>
          <label>
            Motivo e orientação para a pessoa
            <textarea
              value={note}
              maxLength={500}
              rows={3}
              disabled={busy}
              placeholder="Explique o que precisa ser corrigido e como reenviar."
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          {action === "UPLOAD" && (
            <IdentityDocumentUpload
              file={file}
              onChange={setFile}
              onError={setError}
              disabled={busy}
              label="Novo documento do usuário"
            />
          )}
          <p className="signature-help">
            {action === "BLOCK"
              ? "O bloqueio afeta somente as assinaturas. O acesso à conta continua disponível."
              : action === "UPLOAD"
                ? "O aceite anterior será preservado. A substituição será registrada em seu nome, sem atribuir um novo aceite à pessoa."
                : "A pessoa verá o motivo e a etapa solicitada ao acessar o módulo."}
          </p>
          <div className="signature-toolbar">
            <button
              type="button"
              className={
                action === "BLOCK"
                  ? "signature-danger-button"
                  : "signature-primary"
              }
              disabled={
                busy || note.trim().length < 5 || (action === "UPLOAD" && !file)
              }
              onClick={confirm}
            >
              {busy ? "Registrando…" : "Confirmar solicitação"}
            </button>
            <button type="button" disabled={busy} onClick={() => setAction("")}>
              Cancelar
            </button>
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="signature-error">
          {error}
        </p>
      )}
      {success && (
        <p role="status" className="signature-success">
          {success}
        </p>
      )}
    </section>
  );
}
